import React, { useState } from "react";
import { usePageMeta } from "@/lib/seo";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";

const DEBT_TYPES = [
  { v: "personale", l: "Debiti personali" },
  { v: "aziendale", l: "Debiti aziendali" },
  { v: "mutuo", l: "Mutuo / casa a rischio" },
  { v: "fiscale", l: "Cartelle esattoriali" },
];
const AMOUNTS = ["Sotto 20.000€", "20.000 – 50.000€", "50.000 – 150.000€", "150.000 – 500.000€", "Oltre 500.000€"];

export default function Contact() {
  usePageMeta(
    "Contatti — Consulenza Gratuita | Sdebitiamoci Srl",
    "Contatta Sdebitiamoci Srl per una consulenza gratuita sul saldo e stralcio: WhatsApp diretto, email o modulo online. Rispondiamo entro 24 ore."
  );
  const [form, setForm] = useState({ name:"", email:"", phone:"", debt_type:"personale", debt_amount:AMOUNTS[1], situation:"", consent:true });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Serve il consenso al trattamento dati"); return; }
    setLoading(true);
    try {
      await api.post("/consultation", form);
      setSent(true);
      toast.success("Richiesta ricevuta. Ti risponderemo entro 24h.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore nell'invio");
    } finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div className="pt-32 pb-24" data-testid="contact-page">
      <section className="container-editorial grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-5">
          <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2d6fc4]">— Consulenza gratuita</p>
          <h1 className="font-serif text-4xl md:text-6xl leading-[0.9] mt-4 text-[#2d6fc4]">
            Scrivici.<br/><em className="italic text-[#2d6fc4]">Ci sentiamo entro 24h.</em>
          </h1>
          <div className="mt-10 space-y-6">
            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#2d6fc4]">Preferisci parlare?</p>
              <a href="https://wa.me/393927715932" target="_blank" rel="noreferrer" data-testid="whatsapp-link" className="mt-2 inline-flex items-center gap-2 font-serif text-3xl text-[#2d6fc4] link-underline pb-1">
                WhatsApp diretto →
              </a>
            </div>
            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#2d6fc4]">Email</p>
              <a href="mailto:sdebitiamocisrl@libero.it" className="mt-2 inline-block font-serif text-2xl sm:text-3xl break-all text-[#2d6fc4] link-underline pb-1">
                sdebitiamocisrl@libero.it
              </a>
            </div>
            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#2d6fc4]">Preferisci fissare data e ora?</p>
              <Link to="/prenota" data-testid="booking-link" className="mt-2 inline-block font-serif text-3xl text-[#2d6fc4] link-underline pb-1">
                Prenota una chiamata →
              </Link>
            </div>
            <div className="border-t border-[#E5E7EB] pt-6">
              <p className="text-sm text-[#333333] max-w-sm">
                Le informazioni che condividi restano riservate. Le usiamo solo per valutare la tua pratica e prepararci alla prima chiamata.
              </p>
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-7">
          {sent ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="border border-[#E5E7EB] bg-[#F1F5F9] rounded-lg p-10 md:p-16"
              data-testid="form-success"
            >
              <CheckCircle2 size={40} className="text-[#2d6fc4]" strokeWidth={1.5}/>
              <h2 className="font-serif text-4xl md:text-5xl mt-6 leading-tight">Grazie, {form.name.split(" ")[0] || "ci siamo"}.</h2>
              <p className="mt-4 text-lg text-[#333333] max-w-lg">
                Abbiamo ricevuto la tua richiesta. Ti richiameremo entro <strong>24 ore</strong> al numero indicato per una prima analisi — completamente gratuita e senza impegno.
              </p>
              <p className="mt-6 text-sm text-[#333333]">Nel frattempo, puoi anche scriverci su WhatsApp per una risposta immediata.</p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="border border-[#E5E7EB] bg-[#FFFFFF] rounded-lg p-8 md:p-10 space-y-6" data-testid="consultation-form">
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Nome e cognome" required>
                  <input required data-testid="input-name" className="w-full bg-transparent border-b border-[#E5E7EB] py-3 focus:border-[#14417B]" value={form.name} onChange={set("name")}/>
                </Field>
                <Field label="Telefono" required>
                  <input required data-testid="input-phone" className="w-full bg-transparent border-b border-[#E5E7EB] py-3 focus:border-[#14417B]" value={form.phone} onChange={set("phone")}/>
                </Field>
              </div>
              <Field label="Email" required>
                <input required type="email" data-testid="input-email" className="w-full bg-transparent border-b border-[#E5E7EB] py-3 focus:border-[#14417B]" value={form.email} onChange={set("email")}/>
              </Field>
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Tipo di debito">
                  <select data-testid="input-debt-type" className="w-full bg-transparent border-b border-[#E5E7EB] py-3 focus:border-[#14417B]" value={form.debt_type} onChange={set("debt_type")}>
                    {DEBT_TYPES.map(d => <option key={d.v} value={d.v}>{d.l}</option>)}
                  </select>
                </Field>
                <Field label="Importo indicativo">
                  <select data-testid="input-debt-amount" className="w-full bg-transparent border-b border-[#E5E7EB] py-3 focus:border-[#14417B]" value={form.debt_amount} onChange={set("debt_amount")}>
                    {AMOUNTS.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Racconta brevemente la tua situazione">
                <textarea data-testid="input-situation" rows={5} className="w-full bg-transparent border-b border-[#E5E7EB] py-3 focus:border-[#14417B] resize-none" value={form.situation} onChange={set("situation")} placeholder="Es. Ho un mutuo residuo di 180k, sono in ritardo con 8 rate, la banca ha inviato decreto ingiuntivo…"/>
              </Field>
              <label className="flex items-start gap-3 text-sm text-[#333333]">
                <input type="checkbox" data-testid="input-consent" checked={form.consent} onChange={set("consent")} className="mt-1 accent-[#14417B]"/>
                <span>Acconsento al trattamento dei miei dati per essere ricontattato/a. I dati non vengono ceduti a terzi.</span>
              </label>
              <button type="submit" data-testid="submit-consultation" disabled={loading} className="btn-terracotta w-full md:w-auto inline-flex justify-center items-center gap-2 px-8 py-4 rounded-lg text-sm tracking-wide disabled:opacity-60">
                {loading ? "Invio in corso…" : "Invia richiesta gratuita"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#333333]">
      {label} {required && <span className="text-[#2d6fc4]">*</span>}
    </span>
    {children}
  </label>
);
